import { espnService } from './espnService';
import { databaseService, DatabaseGame } from './databaseService';
import { supabase } from '../lib/supabase';
import { API_BASE_URL } from '../lib/config';
import { CURRENT_SEASON, getCurrentNFLWeek } from '../config/season';

export interface PickResult {
  userId: string;
  gameId: number;
  pickValue: string;
  isCorrect: boolean;
  gameScore: {
    homeScore: number;
    awayScore: number;
    homeSpread: number;
  };
}

export interface JobResult {
  gamesProcessed: number;
  picksCalculated: number;
  errors: string[];
  timestamp: string;
}

class PickResultsService {
  
  async processWeeklyGameData(season: number, week: number): Promise<JobResult> {
    const result: JobResult = {
      gamesProcessed: 0,
      picksCalculated: 0,
      errors: [],
      timestamp: new Date().toLocaleString('en-US', { timeZone: 'America/New_York' })
    };

    console.log(`Starting pick calculation job for ${season} season, week ${week}`);

    try {
      // Step 1: Get current week's games from database
      const games = await databaseService.getGames(season, week);
      
      // Step 2: Filter to only Sunday games that haven't been processed yet (check in Eastern Time)
      const sundayGames = games.filter(game => {
        const gameDate = new Date(game.game_date);
        const easternDate = new Date(gameDate.toLocaleString('en-US', { timeZone: 'America/New_York' }));
        return easternDate.getDay() === 0; // Sunday only
      });

      if (sundayGames.length === 0) {
        console.log('No Sunday games found for this week');
        return result;
      }

      // Step 3: Fetch latest scores from ESPN
      const updatedGames = await this.fetchLatestScores(sundayGames, season);
      console.log('Updated games:', updatedGames);
      // Step 4: Update games with new scores
      for (const game of updatedGames) {
        if (game.needsUpdate) {
          await this.updateGameScore(game);
          result.gamesProcessed++;
        }
      }

      // Step 5: Calculate picks for newly final games
      const newlyFinalGames = updatedGames.filter(game =>
        game.is_final && game.needsUpdate
      );

      for (const game of newlyFinalGames) {
        const pickCount = await this.calculatePicksForGame(game);
        result.picksCalculated += pickCount;
      }

      // Step 6: Persist the grading to user_stats (what the Standings page reads)
      // whenever this run actually finalized something new. Chained here so a
      // single "Process Week Data" click keeps standings in sync end to end,
      // instead of requiring a separate "Calculate Stats" click afterward.
      if (newlyFinalGames.length > 0) {
        const statsResult = await this.calculateUserStats(season, week);
        result.errors.push(...statsResult.errors);
      }

      console.log(`Pick calculation job complete: ${result.gamesProcessed} games processed, ${result.picksCalculated} picks calculated`);
      
    } catch (error) {
      console.error('Error in pick calculation job:', error);
      result.errors.push(error instanceof Error ? error.message : 'Unknown error');
    }

    return result;
  }

  private async fetchLatestScores(games: DatabaseGame[], season: number): Promise<(DatabaseGame & { needsUpdate: boolean })[]> {
    const updatedGames: (DatabaseGame & { needsUpdate: boolean })[] = [];

    for (const dbGame of games) {
      try {
        // Get fresh data from ESPN for this specific game
        const espnGames = await espnService.getWeekGames(dbGame.week, season);
        console.log('ESPN games:', espnGames);
        const matchingGame = espnGames.find(g => g.espn_id === dbGame.espn_id);
        console.log('Matching game:', matchingGame);
        if (matchingGame) {
          const needsUpdate = 
            matchingGame.home_score !== dbGame.home_score ||
            matchingGame.away_score !== dbGame.away_score ||
            matchingGame.is_final !== dbGame.is_final;

          updatedGames.push({
            ...dbGame,
            home_score: matchingGame.home_score,
            away_score: matchingGame.away_score,
            is_final: matchingGame.is_final,
            status: matchingGame.status,
            needsUpdate
          });
        } else {
          // Game not found in ESPN response, keep original
          updatedGames.push({ ...dbGame, needsUpdate: false });
        }

        // Small delay to be respectful to ESPN API
        await new Promise(resolve => setTimeout(resolve, 100));

      } catch (error) {
        console.error(`Error fetching score for game ${dbGame.espn_id}:`, error);
        updatedGames.push({ ...dbGame, needsUpdate: false });
      }
    }

    return updatedGames;
  }

  private async updateGameScore(game: DatabaseGame): Promise<void> {
    if (game.home_score === undefined || game.away_score === undefined) {
      return;
    }

    await databaseService.updateGameScores(
      game.espn_id,
      game.home_score,
      game.away_score,
      game.is_final,
      game.status
    );
  }

  private async calculatePicksForGame(game: DatabaseGame): Promise<number> {
    if (!game.is_final || game.home_score === undefined || game.away_score === undefined) {
      return 0;
    }

    try {
      // Get all picks for this game
      const { data: picks, error } = await supabase
        .from('picks')
        .select('id, user_id, pick_value')
        .eq('game_id', game.id);

      if (error) {
        console.error('Error fetching picks:', error);
        return 0;
      }

      if (!picks || picks.length === 0) {
        console.log(`No picks found for game ${game.id}`);
        return 0;
      }

      let correctPicks = 0;

      // Calculate each pick result
      for (const pick of picks) {
        const isCorrect = this.isPickCorrect(
          pick.pick_value,
          game.home_score,
          game.away_score,
          game.home_spread
        );

        if (isCorrect) {
          correctPicks++;
        }
        // NOTE: this method itself only tallies a display count — it doesn't
        // write user_stats. processWeeklyGameData() persists the actual grade
        // afterward via calculateUserStats() (the same admin recompute
        // endpoint "Calculate Stats" uses), once per newly-finalized batch.
      }

      console.log(`Calculated ${picks.length} picks for game ${game.id}: ${correctPicks} correct`);
      return picks.length;

    } catch (error) {
      console.error(`Error calculating picks for game ${game.id}:`, error);
      return 0;
    }
  }

  private isPickCorrect(
    pickValue: string, 
    homeScore: number, 
    awayScore: number, 
    homeSpread: number
  ): boolean {
    const result = this.calculatePickResult(pickValue, homeScore, awayScore, homeSpread);
    return result === 'win';
  }

  // Manual trigger for testing/admin use
  async processCurrentWeekData(season: number = CURRENT_SEASON): Promise<JobResult> {
    // Get current NFL week
    const currentWeek = getCurrentNFLWeek();
    return this.processWeeklyGameData(season, currentWeek);
  }

  // Calculate user statistics for a specific season/week (calls server endpoint)
  async calculateUserStats(season: number, week?: number): Promise<JobResult> {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const token = session?.access_token;
      if (!token) {
        throw new Error('Not authenticated');
      }

      const response = await fetch(`${API_BASE_URL}/.netlify/functions/admin/calculate-user-stats`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ season, week })
      });

      if (!response.ok) {
        throw new Error(`Server error: ${response.status}`);
      }

      const result = await response.json();
      return result;

    } catch (error) {
      console.error('Error calculating user stats:', error);
      return {
        gamesProcessed: 0,
        picksCalculated: 0,
        errors: [error instanceof Error ? error.message : 'Unknown error'],
        timestamp: new Date().toLocaleString('en-US', { timeZone: 'America/New_York' })
      };
    }
  }

  private calculatePickResult(
    pickValue: string,
    homeScore: number,
    awayScore: number,
    homeSpread: number
  ): 'win' | 'lose' | 'push' {
    // Apply spread to home team score
    const homeScoreWithSpread = homeScore + homeSpread;
    
    if (pickValue === 'home') {
      if (homeScoreWithSpread > awayScore) return 'win';
      if (homeScoreWithSpread === awayScore) return 'push';
      return 'lose';
    } else if (pickValue === 'away') {
      if (awayScore > homeScoreWithSpread) return 'win';
      if (awayScore === homeScoreWithSpread) return 'push';
      return 'lose';
    }
    
    return 'lose';
  }
}

export const pickResultsService = new PickResultsService();